import { Inject, Injectable, Optional } from '@angular/core';
import {
  Locale,
  addDays,
  addMonths,
  addYears,
  format,
  formatISO,
  getDate,
  getDay,
  getDaysInMonth,
  getMonth,
  getYear,
  isDate,
  isValid,
  parse,
  parseISO,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { DateAdapter } from './date-adapter';
import { APP_DATE_LOCALE } from './app-date-formats';

const MONTH_FORMATS = { long: 'LLLL', short: 'LLL', narrow: 'LLLLL' };
const DAY_OF_WEEK_FORMATS = { long: 'EEEE', short: 'EEE', narrow: 'EEEEE' };

function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array<T>(length);
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
}

// date-fns-backed DateAdapter (native `Date`, pt-BR by default); ported from the
// Angular Material date-fns adapter.
@Injectable()
export class AppDateFnsAdapter extends DateAdapter<Date, Locale> {
  constructor(@Optional() @Inject(APP_DATE_LOCALE) appDateLocale?: Locale) {
    super();
    this.setLocale(appDateLocale ?? ptBR);
  }

  getYear(date: Date): number {
    return getYear(date);
  }

  getMonth(date: Date): number {
    return getMonth(date);
  }

  getDate(date: Date): number {
    return getDate(date);
  }

  getDayOfWeek(date: Date): number {
    return getDay(date);
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const pattern = MONTH_FORMATS[style];
    return range(12, (i) => this.format(new Date(2017, i, 1), pattern));
  }

  getDateNames(): string[] {
    const dtf =
      typeof Intl !== 'undefined'
        ? new Intl.DateTimeFormat(this.locale.code, {
            day: 'numeric',
            timeZone: 'utc',
          })
        : null;
    return range(31, (i) => {
      if (dtf) {
        const date = new Date();
        date.setUTCFullYear(2017, 0, i + 1);
        date.setUTCHours(0, 0, 0, 0);
        return dtf.format(date).replace(/[\u200E\u200F]/g, '');
      }
      return i + '';
    });
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const pattern = DAY_OF_WEEK_FORMATS[style];
    return range(7, (i) => this.format(new Date(2017, 0, i + 1), pattern));
  }

  getYearName(date: Date): string {
    return this.format(date, 'y');
  }

  getFirstDayOfWeek(): number {
    return this.locale.options?.weekStartsOn ?? 0;
  }

  getNumDaysInMonth(date: Date): number {
    return getDaysInMonth(date);
  }

  clone(date: Date): Date {
    return new Date(date.getTime());
  }

  createDate(year: number, month: number, date: number): Date {
    if (month < 0 || month > 11) {
      throw Error(
        `Invalid month index "${month}". Month index has to be between 0 and 11.`
      );
    }
    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }
    // Using setFullYear/setHours avoids the Date constructor mapping years <100 to 19xx.
    const result = new Date();
    result.setFullYear(year, month, date);
    result.setHours(0, 0, 0, 0);
    if (result.getMonth() !== month) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }
    return result;
  }

  today(): Date {
    return new Date();
  }

  parse(value: any, parseFormat: any): Date | null {
    if (typeof value === 'string' && value.length > 0) {
      const iso8601Date = parseISO(value);
      if (this.isValid(iso8601Date)) {
        return iso8601Date;
      }
      const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];
      if (!parseFormat.length) {
        throw Error('Formats array must not be empty.');
      }
      for (const currentFormat of formats) {
        const fromFormat = parse(value, currentFormat, new Date(), {
          locale: this.locale,
        });
        if (this.isValid(fromFormat)) {
          return this._expandTwoDigitYear(fromFormat);
        }
      }
      return this.invalid();
    } else if (typeof value === 'number') {
      return new Date(value);
    } else if (value instanceof Date) {
      return this.clone(value);
    }
    return null;
  }

  // Match the previous MomentDateAdapter's lenient 2-digit-year handling
  // (e.g. "01/03/24" -> 2024): date-fns parses a 2-digit year literally (year 24),
  // so expand it with Moment's pivot (0-68 -> 2000s, 69-99 -> 1900s).
  private _expandTwoDigitYear(date: Date): Date {
    const year = date.getFullYear();
    if (year >= 0 && year < 100) {
      date.setFullYear(year > 68 ? 1900 + year : 2000 + year);
    }
    return date;
  }

  format(date: Date, displayFormat: any): string {
    if (!this.isValid(date)) {
      throw Error('AppDateFnsAdapter: Cannot format invalid date.');
    }
    return format(date, displayFormat, { locale: this.locale });
  }

  addCalendarYears(date: Date, years: number): Date {
    return addYears(date, years);
  }

  addCalendarMonths(date: Date, months: number): Date {
    return addMonths(date, months);
  }

  addCalendarDays(date: Date, days: number): Date {
    return addDays(date, days);
  }

  toIso8601(date: Date): string {
    return formatISO(date, { representation: 'date' });
  }

  override deserialize(value: any): Date | null {
    if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      const date = parseISO(value);
      if (this.isValid(date)) {
        return date;
      }
    }
    return super.deserialize(value);
  }

  isDateInstance(obj: any): boolean {
    return isDate(obj);
  }

  isValid(date: Date): boolean {
    return isValid(date);
  }

  invalid(): Date {
    return new Date(NaN);
  }
}
